import { useEffect, useState, useMemo } from "react";
import { fetchTvGuide } from "@/lib/api";
import { Link } from "react-router-dom";
import { Radio } from "lucide-react";

// Full-width standalone TV Guide page - 12 hour window
export default function TvGuide() {
  const [data, setData] = useState({ channels: [] });
  useEffect(() => { fetchTvGuide().then(setData); }, []);

  const timeline = useMemo(() => {
    const start = new Date(); start.setMinutes(0, 0, 0);
    return { start, slots: Array.from({ length: 24 }, (_, i) => new Date(start.getTime() + i * 3600_000)) };
  }, [data]);

  const SLOT_W = 220;
  const totalW = SLOT_W * timeline.slots.length;
  const nowPx = Math.max(0, ((new Date() - timeline.start) / 3600_000) * SLOT_W);

  return (
    <div className="pt-16" data-testid="tv-guide-page">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center gap-2 mb-2"><Radio className="w-4 h-4 text-[#D4AF37]"/><span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-[#D4AF37]">Full Schedule</span></div>
        <h1 className="font-heading text-4xl md:text-6xl font-bold mb-8 leading-none">TV Guide</h1>

        <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl overflow-hidden">
          <div className="flex">
            <div className="w-56 flex-shrink-0 border-r border-white/10 bg-[#050507]/60">
              <div className="h-11 border-b border-white/10 flex items-center px-4"><span className="text-[10px] text-white/40 uppercase tracking-widest">Channel</span></div>
              {data.channels.map(ch => (
                <Link key={ch.id} to={`/live/${ch.slug}`} className="flex items-center gap-3 px-4 py-4 border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`guide-ch-${ch.slug}`}>
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10 flex-shrink-0 border border-white/10"><img src={ch.logo_url} alt="" className="w-full h-full object-cover"/></div>
                  <div className="min-w-0"><p className="font-heading text-white font-semibold truncate">{ch.name}</p><p className="text-[11px] text-white/50 truncate">{ch.tagline}</p></div>
                </Link>
              ))}
            </div>
            <div className="flex-1 overflow-x-auto row-scroll">
              <div style={{width: totalW, minWidth: totalW}} className="relative">
                <div className="h-11 border-b border-white/10 flex sticky top-0 bg-[#0A0A0E] z-10">
                  {timeline.slots.map((d, i) => (
                    <div key={i} style={{width: SLOT_W}} className="border-r border-white/5 flex items-center px-3"><span className="text-xs text-white/60 font-medium tracking-wide">{d.toLocaleTimeString([], {hour:'numeric',minute:'2-digit'})}</span></div>
                  ))}
                </div>
                {data.channels.map(ch => <GuideRow key={ch.id} channel={ch} timelineStart={timeline.start} slotWidth={SLOT_W} totalWidth={totalW} />)}
                {nowPx > 0 && nowPx < totalW && (
                  <div className="absolute top-11 bottom-0 w-px bg-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,0.8)] z-20 pointer-events-none" style={{left: nowPx}}>
                    <div className="w-3 h-3 -ml-[5px] -mt-[6px] rounded-full bg-[#D4AF37]"/>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GuideRow({ channel, timelineStart, slotWidth, totalWidth }) {
  const now = new Date();
  return (
    <div className="relative h-24 border-b border-white/5">
      {(channel.schedule || []).map(p => {
        const st = new Date(p.start_time); const en = new Date(st.getTime() + p.duration_min * 60_000);
        const offset = (st - timelineStart) / 3600_000;
        const left = offset * slotWidth; const width = (p.duration_min / 60) * slotWidth;
        if (left + width < 0 || left > totalWidth) return null;
        const isNow = now >= st && now < en;
        return (
          <div key={p.id} className={`epg-block absolute top-2 bottom-2 px-3 py-2 rounded-lg border overflow-hidden bg-white/5 border-white/10 ${isNow ? "now" : ""}`} style={{left: Math.max(0, left), width: Math.min(width, totalWidth - Math.max(0, left))}} data-testid={`guide-prog-${p.id}`}>
            <p className="font-heading text-sm text-white font-semibold truncate">{p.title}</p>
            <p className="text-[11px] text-white/50 truncate">{p.host || p.description}</p>
            <p className="text-[10px] text-white/40 mt-1">{st.toLocaleTimeString([],{hour:'numeric',minute:'2-digit'})} · {p.duration_min}m</p>
          </div>
        );
      })}
    </div>
  );
}
